/**
 * Testable abstraction for media track management.
 * All MediaStream/MediaStreamTrack operations go through this module so they can be mocked in tests.
 */

import { getZoomRange } from "../utils";

/**
 * @param {MediaStream} stream
 * @returns {MediaStreamTrack | null}
 */
export function getVideoTrack(stream) {
  if (!stream?.getVideoTracks) return null;
  const tracks = stream.getVideoTracks();
  return tracks.length > 0 ? tracks[0] : null;
}

/**
 * @param {MediaStreamTrack} track
 * @returns {MediaTrackCapabilities}
 */
export function getCapabilities(track) {
  if (!track?.getCapabilities) return {};
  try {
    return track.getCapabilities() ?? {};
  } catch {
    return {};
  }
}

/**
 * @param {MediaStreamTrack} track
 * @returns {boolean}
 */
export function isTorchSupported(track) {
  return getCapabilities(track).torch === true;
}

/**
 * @param {MediaStreamTrack} track
 * @returns {{ min: number, max: number, step: number } | null}
 */
export function getZoomRangeFromTrack(track) {
  return getZoomRange(getCapabilities(track));
}

/**
 * @param {MediaStreamTrack} track
 * @param {boolean} on
 * @returns {Promise<void>}
 */
export function setTorch(track, on) {
  if (!track?.applyConstraints) return Promise.resolve();
  if (!isTorchSupported(track)) return Promise.resolve();
  return track.applyConstraints({ advanced: [{ torch: on }] }).catch(() => {});
}

/**
 * @param {MediaStreamTrack} track
 * @param {number} level
 * @returns {Promise<void>}
 */
export function setZoom(track, level) {
  if (!track?.applyConstraints) return Promise.resolve();
  const range = getZoomRangeFromTrack(track);
  if (!range) return Promise.resolve();
  const clamped = Math.min(Math.max(level, range.min), range.max);
  return track.applyConstraints({ advanced: [{ zoom: clamped }] }).catch(() => {});
}

/**
 * Stop a single track. Idempotent.
 * @param {MediaStreamTrack} track
 */
export function stopTrack(track) {
  if (track?.stop && track.readyState !== "ended") {
    try {
      track.stop();
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Stop track failed:", err);
      }
    }
  }
}

/**
 * Stop all tracks in a stream. Idempotent.
 * @param {MediaStream | null} stream
 */
export function stopStream(stream) {
  if (!stream?.getTracks) return;
  stream.getTracks().forEach(stopTrack);
}
